import type { RequestFn } from '../http';
import type {
  BulkSyncResult,
  SyncStatistics,
  TicketSyncState,
} from '../types';

export type SyncAreaKey = 'tickets';

export interface StagedActionResult {
  entityId: string;
  action: string;
  success: boolean;
  cwTicketId?: number;
  error?: string;
  details?: Record<string, unknown>;
}

export interface StagedAreaResult {
  area: SyncAreaKey;
  runId: string;
  dryRun: boolean;
  processed: number;
  succeeded: number;
  failed: number;
  actions: StagedActionResult[];
  error?: string;
}

export interface SyncDryRunEntry {
  id: string;
  area: SyncAreaKey;
  entityId: string;
  plannedAction: string;
  payload: Record<string, unknown>;
  runId: string | null;
  note: string | null;
  createdAt: string;
}

export interface ArticleSyncResult {
  synced: number;
  created: number;
  updated: number;
  errors: string[];
}

export function createSyncApi(request: RequestFn) {
  return {
    // Weclapp → nativer Artikelstamm
    async syncArticles() {
      return request<ArticleSyncResult>('/sync/articles', { method: 'POST' });
    },

    async bulkSyncWeclappTickets(data: {
      customerId?: string;
      statusIds?: string[];
      dryRun?: boolean;
      limit?: number;
    }) {
      return request<BulkSyncResult>('/sync/weclapp-bulk', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async getSyncStatistics() {
      return request<SyncStatistics>('/sync/statistics');
    },

    async getSyncHistory(
      options: { limit?: number; offset?: number; state?: TicketSyncState } = {}
    ) {
      const params = new URLSearchParams();
      if (options.limit) params.set('limit', String(options.limit));
      if (options.offset) params.set('offset', String(options.offset));
      if (options.state) params.set('state', options.state);
      const query = params.toString() ? `?${params.toString()}` : '';
      return request<{
        history: Array<{
          id: string;
          weclappTicketId: string;
          weclappTicketNumber?: string;
          weclappSubject?: string;
          cwTicketId?: number;
          syncState: TicketSyncState;
          lastSyncAt?: string;
          lastError?: string;
          createdAt: string;
          mappingProfile?: { id: string; name: string };
          attachments: Array<{
            id: string;
            filename: string;
            syncState: TicketSyncState;
            cwDocumentId?: number;
          }>;
        }>;
        total: number;
        limit: number;
        offset: number;
      }>(`/sync/history${query}`);
    },

    async getQueueJobs() {
      return request<{
        available: boolean;
        message?: string;
        queues: Array<{
          name: string;
          label: string;
          counts: Record<string, number>;
          failed: Array<{
            id: string;
            submissionId?: string;
            attemptsMade: number;
            failedReason?: string;
            timestamp?: number;
            finishedOn?: number;
          }>;
        } | null>;
      }>('/admin/sync/jobs');
    },

    async retryQueueJob(submissionId: string) {
      return request<{ success: boolean; message: string }>(
        `/admin/sync/jobs/${submissionId}/retry`,
        { method: 'POST' },
      );
    },

    // ── Staged Sync (Weclapp → CW pro Bereich) ─────────────────────────────
    async getSyncAreas() {
      return request<{
        areas: Record<
          SyncAreaKey,
          {
            enabled: boolean;
            dryRun: boolean;
            lastRunAt?: string;
            lastError?: string | null;
            lastRunSummary?: Record<string, unknown>;
          }
        >;
        dryRun: Record<SyncAreaKey, { count: number; latestRunAt: string | null; latestRunId: string | null }>;
      }>('/admin/sync/areas');
    },

    async updateSyncArea(
      area: SyncAreaKey,
      patch: { enabled?: boolean; dryRun?: boolean }
    ) {
      return request<{
        success: boolean;
        areas: Record<SyncAreaKey, { enabled: boolean; dryRun: boolean }>;
      }>(`/admin/sync/areas/${area}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
    },

    async runSyncArea(area: SyncAreaKey, options: { dry?: boolean; limit?: number } = {}) {
      const params = options.dry ? '?dry=true' : '';
      return request<StagedAreaResult>(`/admin/sync/areas/${area}/run${params}`, {
        method: 'POST',
        body: JSON.stringify({ limit: options.limit }),
      });
    },

    async testSyncArea(area: SyncAreaKey, options: { weclappId?: string } = {}) {
      return request<StagedAreaResult>(`/admin/sync/areas/${area}/test`, {
        method: 'POST',
        body: JSON.stringify(options),
      });
    },

    async getSyncAreaDryRunLog(area: SyncAreaKey, options: { runId?: string; limit?: number } = {}) {
      const params = new URLSearchParams();
      if (options.runId) params.set('runId', options.runId);
      if (options.limit) params.set('limit', String(options.limit));
      const query = params.toString() ? `?${params.toString()}` : '';
      return request<{ entries: SyncDryRunEntry[] }>(
        `/admin/sync/areas/${area}/dry-run-log${query}`
      );
    },

    async syncSubmissions(data: {
      submissions: Array<{
        submissionId: string;
        formType: 'ONSITE_SERVICE' | 'ONSITE_DELIVERY';
        data: Record<string, any>;
        localTimestamp: number;
        remoteTimestamp?: number;
      }>;
    }) {
      return request<{
        synced: number;
        failed: number;
        conflicted: number;
        results: Array<{
          submissionId: string;
          success: boolean;
          conflicted?: boolean;
          /** Bericht ist inzwischen eingereicht — dieser Zwischenstand kommt nie mehr an, Eintrag verwerfen. */
          obsolete?: boolean;
          remoteTimestamp?: number;
          syncedAt?: number;
          status?: string;
          message?: string;
          /** Rohe Server-Fehlermeldung für "Technische Details" (message ist humanisiert). */
          technicalDetail?: string;
        }>;
      }>('/sync/submissions', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
  };
}
